import { useState, useEffect } from "react";
import { useAuth } from "../lib/auth";
import { fmtMoneyInput } from "./format";
import { DocUploader, useBackdropClose } from "./deskui";
import { getDraft, setDraft, clearDraft } from "./drafts";

// Intake — ingestion path 3, the owner naming a building and handing in the paperwork. Four steps:
// name it (owner-stated identity), the county shell (honest fail-fast, never "no record found"),
// how they are holding it (disposition + the optional floor that only appears for "actively selling"),
// then the upload (fail-closed on the shared scan gate until a scanner is wired). Copy is Juno's voice.

export function IntakeWizard({ onClose, onChanged }) {
  const { authedFetch, session } = useAuth();
  const acct = session?.account_id;
  const backdrop = useBackdropClose(onClose);   // F25: drag-select never closes the panel
  // F25: restore the typed draft via a useState initializer (runs before the save-effect below), so a
  // reopen in the same session brings the fields back. In-memory only — never storage (drafts.js).
  const _d0 = (() => { try { return JSON.parse(getDraft(acct, "intake_wizard") || "{}"); } catch { return {}; } })();
  const [step, setStep] = useState(1);
  const [ref, setRef] = useState(null);
  const [address, setAddress] = useState(_d0.address || "");
  const [name, setName] = useState(_d0.name || "");
  const [ownerNumber, setOwnerNumber] = useState(_d0.ownerNumber || "");
  const [assetType, setAssetType] = useState(_d0.assetType || "");
  const [subtype, setSubtype] = useState(_d0.subtype || "");
  const [buildingClass, setBuildingClass] = useState(_d0.buildingClass || "");
  const [submarket, setSubmarket] = useState(_d0.submarket || "");
  const [disposition, setDisposition] = useState(null);
  const [floor, setFloor] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [filed, setFiled] = useState(false);
  // A SUGGESTION only (founder ruling 7 Sep): the fields rest EMPTY. "Same again" fills all four, so the
  // owner CHOSE it and human_stated stays honest — silence is never a statement.
  const [suggest, setSuggest] = useState(null);
  const [suggestDismissed, setSuggestDismissed] = useState(false);

  useEffect(() => {
    authedFetch("/owner/intake/last-classification").then(setSuggest).catch(() => {});
  }, [authedFetch]);

  // F25: keep the typed draft across an accidental close/reopen in the SAME session (in-memory,
  // account-scoped, never storage). Restore happens in the useState initializers above; this only
  // persists, and stops once the building is created.
  useEffect(() => {
    if (ref) return;   // building created — the draft's job is done
    setDraft(acct, "intake_wizard", JSON.stringify(
      { address, name, ownerNumber, assetType, subtype, buildingClass, submarket }));
  }, [acct, ref, address, name, ownerNumber, assetType, subtype, buildingClass, submarket]);

  const SUBTYPE_LABEL = {
    neighborhood_anchored: "neighborhood center", grocery_anchored: "grocery-anchored center",
    unanchored_strip: "unanchored strip", single_tenant_nnn: "single-tenant (NNN)",
  };
  const ASSET_LABEL = { retail: "retail", retail_mixed: "mixed-use retail", office: "office", medical: "medical" };
  const suggestText = suggest && [ASSET_LABEL[suggest.asset_type], SUBTYPE_LABEL[suggest.subtype],
    suggest.building_class && "Class " + suggest.building_class, suggest.submarket]
    .filter(Boolean).join(", ");
  const applySuggestion = () => {
    if (!suggest) return;
    setAssetType(suggest.asset_type || "");
    setSubtype(suggest.subtype || "");
    setBuildingClass(suggest.building_class || "");
    setSubmarket(suggest.submarket || "");
    setSuggestDismissed(true);
  };

  const num = (v) => (v.trim() === "" ? null : Number(v.replace(/[^0-9]/g, "")));

  const createBuilding = async () => {
    if (!address.trim()) { setErr("Name the building first."); return; }
    setBusy(true); setErr("");
    try {
      const d = await authedFetch("/owner/commands/building",
        { method: "POST", body: { address: address.trim(), name: name.trim() || null,
                                  owner_number: num(ownerNumber),
                                  asset_type: assetType || null, subtype: subtype || null,
                                  building_class: buildingClass || null,
                                  submarket: submarket.trim() || null } });
      clearDraft(acct, "intake_wizard");
      setRef(d.building_ref); onChanged && onChanged(); setStep(2);
    } catch (e) { setErr(e.detail || "Could not add the building."); } finally { setBusy(false); }
  };

  const saveDisposition = async () => {
    if (!disposition) { setErr("Pick how you are holding it."); return; }
    setBusy(true); setErr("");
    try {
      await authedFetch(`/owner/commands/building/${ref}/disposition`,
        { method: "POST", body: { disposition, floor: disposition === "selling" ? num(floor) : null } });
      onChanged && onChanged(); setStep(4);
    } catch (e) { setErr(e.detail || "Could not save that."); } finally { setBusy(false); }
  };

  const finish = () => { onChanged && onChanged(); onClose(); };

  return (
    <div className="panel-scrim" data-testid="intake-wizard" {...backdrop}>
      <aside className="panel wiz" onClick={(e) => e.stopPropagation()}>
        <div className="panel-top">
          <div className="panel-ref">Add a building</div>
          <button className="panel-close" data-testid="wiz-close" onClick={onClose}>Close</button>
        </div>
        <div className="wiz-steps">Step {step} of 4</div>

        {step === 1 && (
          <div className="wiz-body" data-testid="wiz-step-name">
            <div className="mlog-eyebrow">WHICH BUILDING IS YOURS</div>
            <p className="wiz-lead">Tell me which building this is. You are the authority on what you
              own — the paperwork tells me what is inside it, not whose it is.</p>
            <label className="mlog-label">Address</label>
            <input className="mlog-input" data-testid="wiz-address" value={address} autoFocus
                   placeholder="e.g. 11902 Bissonnet Ave, Houston TX"
                   onChange={(e) => setAddress(e.target.value)} />
            <label className="mlog-label">Name <span className="set-opt">optional</span></label>
            <input className="mlog-input" data-testid="wiz-name" value={name}
                   placeholder="a name you'll recognise, e.g. Huntwick Plaza"
                   onChange={(e) => setName(e.target.value)} />
            <p className="wiz-hint">A label only — the address stays how I identify the building. You can
              add or change it any time.</p>
            <label className="mlog-label">What you think it is worth (optional)</label>
            <input className="mlog-input" data-testid="wiz-owner-number" value={fmtMoneyInput(ownerNumber)} inputMode="numeric"
                   placeholder="your number, if you have one"
                   onChange={(e) => setOwnerNumber(e.target.value.replace(/[^0-9]/g, ""))} />
            <p className="wiz-hint">You know what your building is — telling me now means the mark rests
              on your word, not a guess. Leave it blank if you'd rather not say.</p>
            <div className="wiz-what" data-testid="wiz-what-is-it">
              <div className="mlog-eyebrow">WHAT IS IT</div>
              {suggest && suggestText && !suggestDismissed && (
                <div className="wiz-suggest" data-testid="wiz-suggest">
                  <span className="wiz-suggest-text">Your last building was {suggestText}.</span>
                  <span className="wiz-suggest-actions">
                    <button className="wiz-suggest-yes" data-testid="wiz-suggest-apply"
                            onClick={applySuggestion}>Same again</button>
                    <button className="wiz-suggest-no" data-testid="wiz-suggest-dismiss"
                            onClick={() => setSuggestDismissed(true)}>No, different</button>
                  </span>
                </div>
              )}
              <div className="wiz-what-grid">
                <div>
                  <label className="mlog-label">Kind</label>
                  <select className="mlog-input" data-testid="wiz-asset-type" value={assetType}
                          onChange={(e) => { setAssetType(e.target.value); setSubtype(""); }}>
                    <option value="">— select —</option>
                    <option value="retail">Retail</option>
                    <option value="retail_mixed">Retail + other (mixed use)</option>
                    <option value="office">Office</option>
                    <option value="medical">Medical</option>
                  </select>
                </div>
                {(assetType === "retail" || assetType === "retail_mixed") && (
                  <div>
                    <label className="mlog-label">Type</label>
                    <select className="mlog-input" data-testid="wiz-subtype" value={subtype}
                            onChange={(e) => setSubtype(e.target.value)}>
                      <option value="">— select —</option>
                      <option value="neighborhood_anchored">Neighborhood center (anchored)</option>
                      <option value="grocery_anchored">Grocery-anchored center</option>
                      <option value="unanchored_strip">Unanchored strip</option>
                      <option value="single_tenant_nnn">Single-tenant (NNN)</option>
                    </select>
                  </div>
                )}
                <div>
                  <label className="mlog-label">Class</label>
                  <select className="mlog-input" data-testid="wiz-class" value={buildingClass}
                          onChange={(e) => setBuildingClass(e.target.value)}>
                    <option value="">— select —</option>
                    <option value="A">A</option>
                    <option value="B">B</option>
                    <option value="C">C</option>
                  </select>
                </div>
                <div>
                  <label className="mlog-label">Submarket / area</label>
                  <input className="mlog-input" data-testid="wiz-submarket" value={submarket}
                         placeholder="e.g. Katy corridor, Pearland"
                         onChange={(e) => setSubmarket(e.target.value)} />
                </div>
              </div>
            </div>
            <button className="mlog-primary" data-testid="wiz-add-building" disabled={busy || !address.trim()}
                    onClick={createBuilding}>Add the building</button>
          </div>
        )}

        {step === 2 && (
          <div className="wiz-body" data-testid="wiz-step-county">
            <div className="mlog-eyebrow">THE COUNTY RECORD</div>
            <div className="wiz-note" data-testid="wiz-county-note">
              I cannot check the county record yet. Give me the paperwork and I will build the file
              from that.
            </div>
            <button className="mlog-primary" data-testid="wiz-county-continue" onClick={() => setStep(3)}>Continue</button>
          </div>
        )}

        {step === 3 && (
          <div className="wiz-body" data-testid="wiz-step-disposition">
            <div className="mlog-eyebrow">HOW ARE YOU HOLDING IT</div>
            <button className={"wiz-opt" + (disposition === "selling" ? " wiz-opt-on" : "")}
                    data-testid="wiz-disp-selling" onClick={() => setDisposition("selling")}>
              <span className="wiz-opt-title">Actively selling</span>
              <span className="wiz-opt-sub">You want it sold.</span>
            </button>
            <button className={"wiz-opt" + (disposition === "open" ? " wiz-opt-on" : "")}
                    data-testid="wiz-disp-open" onClick={() => setDisposition("open")}>
              <span className="wiz-opt-title">Open to price</span>
              <span className="wiz-opt-sub">Not selling, but the right number would move you.</span>
            </button>
            {disposition === "selling" && (
              <div className="wiz-floor" data-testid="wiz-floor-field">
                <label className="mlog-label">The least you would take (optional)</label>
                <input className="mlog-input" data-testid="wiz-floor" value={floor} inputMode="numeric"
                       placeholder="leave blank — it costs you nothing" onChange={(e) => setFloor(e.target.value)} />
                <p className="wiz-hint">Different from what you think it is worth. Blank is a complete
                  answer; it only ever ranks options, and never hides one.</p>
              </div>
            )}
            <button className="mlog-primary" data-testid="wiz-save-disposition" disabled={busy || !disposition}
                    onClick={saveDisposition}>Save</button>
          </div>
        )}

        {step === 4 && (
          <div className="wiz-body" data-testid="wiz-step-upload">
            <div className="mlog-eyebrow">HAND ME THE PAPERWORK</div>
            <p className="wiz-lead">An offering memo, a rent roll, a single PDF or .xlsx (.xls isn't
              readable — save as .xlsx) — whatever you have. Every line of the mark will trace back to a
              page you gave me.</p>
            <p className="wiz-subprocessors" data-testid="wiz-subprocessor-note">
              Your document is stored by our platform provider, scanned for malware by a scanning
              service, and read by Anthropic's API to extract the figures. Each of those uses its own
              infrastructure providers.
            </p>
            <DocUploader buildingRef={ref} onFiled={() => setFiled(true)} testidPrefix="wiz" />
            <button className="mlog-primary" data-testid="wiz-finish" onClick={finish}>
              {filed ? "Done" : "Done for now"}
            </button>
          </div>
        )}

        {err && <div className="mlog-error" data-testid="wiz-error">{err}</div>}
      </aside>
    </div>
  );
}
